import { Tag } from "antd";
import { AxiosResponse } from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { GET_CUSTOMER_NAME_AND_SEGMENT_QUERY } from "../gql/customers";
import { request } from "../helpers/utilities";
import "../styles/components/CustomerPageTitle.css";

type FetchState = {
  loading: boolean;
  error: boolean;
  data: {
    fullname: string | null;
    segment: string | null;
  };
};

export default function CustomerPageTitle(): JSX.Element {
  const location = useLocation();
  const [fetchState, setFetchState] = useState<FetchState>({
    loading: false,
    error: false,
    data: {
      fullname: null,
      segment: null,
    },
  });

  const customerId = useCallback(() => {
    const match = location.pathname.match(/^\/customers\/([^/]+)$/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const fetchCustomerName = useCallback(async () => {
    const id = customerId();
    if (!id) return;

    setFetchState((prev) => ({ ...prev, loading: true, error: false }));

    try {
      const response: AxiosResponse = await request({
        query: GET_CUSTOMER_NAME_AND_SEGMENT_QUERY,
        variables: { customerId: id },
      });

      const customerData = response.data?.data?.customer;

      if (customerData?.fullname) {
        setFetchState({
          loading: false,
          error: false,
          data: {
            fullname: customerData.fullname,
            segment: customerData.customerSegment || null,
          },
        });
      } else {
        setFetchState({
          loading: false,
          error: true,
          data: { fullname: null, segment: null },
        });
      }
    } catch (error) {
      setFetchState({
        loading: false,
        error: true,
        data: { fullname: null, segment: null },
      });
    }
  }, [customerId]);

  useEffect(() => {
    fetchCustomerName();
  }, [fetchCustomerName]);

  const getSegmentColor = (segment: string | null): string => {
    if (!segment) return "blue";

    const mapSegmentToClass: Record<string, string> = {
      silver: "#757575",
      diamond: "#81c784",
      platinum: "#000000",
      bronze: "#ffeb3b",
    };

    const segmentLower = segment.toLowerCase();
    return mapSegmentToClass[segmentLower] || "blue";
  };

  if (fetchState.error) {
    return (
      <span className="customer-title error">
        <button
          className="retry-button"
          onClick={(e) => {
            e.preventDefault();
            fetchCustomerName();
          }}
          aria-label="Retry loading customer name"
        >
          Retry
        </button>
      </span>
    );
  }

  return (
    <span className="customer-title">
      {fetchState.loading ? (
        "Loading..."
      ) : (
        <>
          {fetchState.data.fullname}
          {fetchState.data.segment && (
            <Tag
              color={getSegmentColor(fetchState.data.segment)}
              style={{ marginLeft: "8px" }}
            >
              {fetchState.data.segment}
            </Tag>
          )}
        </>
      )}
    </span>
  );
}
